import type { MutableRefObject } from 'react';
import { useRef } from 'react';

export type AssignableRef<T> =
	((current: T) => void) | { current: T } | null | undefined;

export function assignRef<T>(ref: AssignableRef<T>, value: T) {
	if (!ref) return;
	if (typeof ref === 'function') {
		ref(value);
	} else {
		try {
			ref.current = value;
		} catch {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string
			throw new Error(`Cannot assign value "${value}" to ref "${ref}"`);
		}
	}
}

function makeForwardingRef<T>(initialValue: T, refs: AssignableRef<T>[]) {
	let current = initialValue;

	return {
		get current() {
			return current;
		},

		set current(newValue: T) {
			current = newValue;

			for (const ref of refs) {
				assignRef(ref, newValue);
			}
		},
	};
}

/**
 * The same as `useRef` but any changes to the current value are forwarded to the given refs.
 */
export function useForwardingRef<T>(
	initialValue: T,
	refs: AssignableRef<T>[]
): MutableRefObject<T> {
	// Use a ref of the forwarding ref so that the same instance of the
	// forwarding ref is used throughout the life-time of the component.
	// Note, `useMemo` will not work because `useMemo` is not guaranteed
	// to always keep the same instance every render.
	const ref = useRef(makeForwardingRef(initialValue, refs));
	return ref.current;
}
